import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { PasswordInput } from '../../components/Inputs/PasswordInput';
import Strings from '../../Strings';
import {
  actions,
  DELETE_SAML_LINK_RESULT,
  GET_SAML_LINK_STATUS_RESULT,
  START_SAML_LINK_RESULT,
} from './redux/actions';
import './SamlLinkSettings.scss';

const S = Strings as any;

interface SamlLinkStatus {
  org_id: string;
  provider_enabled: boolean;
  linked: boolean;
  subject?: string;
  linked_at?: string;
  last_login_at?: string;
}

interface SamlLinkSettingsProps {
  getSamlLinkStatus: () => Promise<{ type: string; response: SamlLinkStatus }>;
  startSamlLink: (currentPassword: string) => Promise<{ type: string; response?: { redirect_url: string; expires_at: string } }>;
  deleteSamlLink: (currentPassword: string) => Promise<{ type: string }>;
}

const SSO_LINK_ERROR_MESSAGES: Record<string, string> = {
  link_expired: 'The SSO linking session has expired. Please try again.',
  subject_taken: 'This SSO identity is already linked to another account.',
  already_linked: 'Your account already has a different SSO identity linked.',
  internal_error: 'An unexpected error occurred during SSO linking. Please try again.',
};

function SamlLinkSettings(props: SamlLinkSettingsProps) {
  const { getSamlLinkStatus, startSamlLink, deleteSamlLink } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [linkStatus, setLinkStatus] = useState<SamlLinkStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [modalMode, setModalMode] = useState<'link' | 'unlink' | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);

  useEffect(() => {
    checkReturnParams();
    loadLinkStatus();
  }, []);

  const checkReturnParams = () => {
    const query = new URLSearchParams(window.location.search);
    const ssoLinkSuccess = query.get('ssoLink');
    const ssoLinkError = query.get('ssoLinkError');

    if (ssoLinkSuccess === 'success') {
      setSuccessMessage('Your SSO account has been linked successfully.');
    } else if (ssoLinkError) {
      setError(SSO_LINK_ERROR_MESSAGES[ssoLinkError] || `SSO linking failed: ${ssoLinkError}`);
    }
  };

  const loadLinkStatus = async () => {
    setIsLoading(true);
    try {
      const response = await getSamlLinkStatus();
      if (response.type === GET_SAML_LINK_STATUS_RESULT) {
        setLinkStatus(response.response);
      }
    } catch {
      // If the endpoint fails (e.g. no provider), we just won't show the section
    }
    setIsLoading(false);
  };

  const handleLinkStart = async () => {
    if (!currentPassword) {
      setError('Password is required');
      return;
    }
    setLinkLoading(true);
    setError(null);
    try {
      const response = await startSamlLink(currentPassword);
      if (response.type === START_SAML_LINK_RESULT && response.response) {
        window.location.href = response.response.redirect_url;
        return;
      }
      setError('Failed to start SSO linking. Please check your password and try again.');
    } catch {
      setError('Failed to start SSO linking. Please check your password and try again.');
    }
    setLinkLoading(false);
  };

  const handleUnlink = async () => {
    if (!currentPassword) {
      setError('Password is required');
      return;
    }
    setLinkLoading(true);
    setError(null);
    try {
      const response = await deleteSamlLink(currentPassword);
      if (response.type === DELETE_SAML_LINK_RESULT) {
        setSuccessMessage('Your SSO account has been disconnected.');
        setModalMode(null);
        setCurrentPassword('');
        await loadLinkStatus();
        setLinkLoading(false);
        return;
      }
      setError('Failed to disconnect SSO. Please check your password and try again.');
    } catch {
      setError('Failed to disconnect SSO. Please check your password and try again.');
    }
    setLinkLoading(false);
  };

  const handleModalClose = () => {
    setModalMode(null);
    setCurrentPassword('');
    setError(null);
  };

  const formatTimestamp = (ts: string | undefined) => {
    if (!ts) return null;
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  // Don't render while loading
  if (isLoading) return null;

  // Don't render if no status (endpoint not available) or provider not enabled
  if (!linkStatus || !linkStatus.provider_enabled) return null;

  return (
    <div className="saml-link-settings" data-testid="saml-link-settings">
      <h3 className="saml-link-settings__title">Single Sign-On</h3>

      {successMessage && (
        <div className="saml-link-settings__success" data-testid="sso-link-success-banner">
          {successMessage}
        </div>
      )}

      {error && !modalMode && (
        <div className="saml-link-settings__error" data-testid="sso-link-error-banner">
          {error}
        </div>
      )}

      <div className="saml-link-settings__content">
        {linkStatus.linked ? (
          <div className="saml-link-settings__linked-state" data-testid="sso-linked-state">
            <div className="saml-link-settings__status-row">
              <span className="saml-link-settings__label">Status</span>
              <span className="saml-link-settings__status-badge saml-link-settings__status-badge--enabled">
                Connected
              </span>
            </div>
            <div className="saml-link-settings__status-row">
              <span className="saml-link-settings__label">SSO Identity</span>
              <span className="saml-link-settings__value" data-testid="sso-link-subject">
                {linkStatus.subject}
              </span>
            </div>
            <div className="saml-link-settings__status-row">
              <span className="saml-link-settings__label">Linked</span>
              <span className="saml-link-settings__value" data-testid="sso-link-linked-at">
                {formatTimestamp(linkStatus.linked_at)}
              </span>
            </div>
            {linkStatus.last_login_at && (
              <div className="saml-link-settings__status-row">
                <span className="saml-link-settings__label">Last SSO Login</span>
                <span className="saml-link-settings__value" data-testid="sso-link-last-login">
                  {formatTimestamp(linkStatus.last_login_at)}
                </span>
              </div>
            )}
            <div className="saml-link-settings__actions">
              <button
                type="button"
                className="saml-link-settings__button saml-link-settings__button--secondary"
                onClick={() => { setModalMode('unlink'); setError(null); setSuccessMessage(null); }}
                data-testid="sso-unlink-start-button"
              >
                Disconnect SSO
              </button>
            </div>
          </div>
        ) : (
          <div className="saml-link-settings__unlinked-state" data-testid="sso-unlinked-state">
            <p className="saml-link-settings__description">
              Link your account to your organization's SSO provider for secure single sign-on access.
            </p>
            <button
              type="button"
              className="saml-link-settings__button saml-link-settings__button--primary"
              onClick={() => { setModalMode('link'); setError(null); setSuccessMessage(null); }}
              data-testid="sso-link-start-button"
            >
              Link SSO Account
            </button>
          </div>
        )}
      </div>

      {modalMode && (
        <div
          className="saml-link-settings__modal"
          data-testid={modalMode === 'link' ? 'sso-link-password-modal' : 'sso-unlink-password-modal'}
        >
          <div className="saml-link-settings__modal-content">
            <h3>{modalMode === 'link' ? 'Link SSO Account' : 'Disconnect SSO Account'}</h3>
            <p>
              {modalMode === 'link'
                ? 'Enter your current password to verify your identity before linking your SSO account.'
                : 'Enter your current password to verify your identity before disconnecting your SSO account.'}
            </p>

            {error && (
              <div className="saml-link-settings__error">
                {error}
              </div>
            )}

            <div className="saml-link-settings__password-section">
              <label htmlFor="sso-link-password">Current Password</label>
              <PasswordInput
                id="sso-link-password"
                value={currentPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                align="left"
                data-testid="sso-link-password-input"
              />
            </div>

            <div className="saml-link-settings__button-group">
              <button
                type="button"
                className="saml-link-settings__button saml-link-settings__button--secondary"
                onClick={handleModalClose}
                disabled={linkLoading}
              >
                {S.cancel || 'Cancel'}
              </button>
              <button
                type="button"
                className={`saml-link-settings__button ${
                  modalMode === 'link'
                    ? 'saml-link-settings__button--primary'
                    : 'saml-link-settings__button--danger'
                }`}
                disabled={!currentPassword || linkLoading}
                onClick={modalMode === 'link' ? handleLinkStart : handleUnlink}
                data-testid={modalMode === 'link' ? 'sso-link-confirm-button' : 'sso-unlink-confirm-button'}
              >
                {modalMode === 'link'
                  ? (linkLoading ? 'Linking...' : 'Link Account')
                  : (linkLoading ? 'Disconnecting...' : 'Disconnect SSO')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const mapDispatchToProps = (dispatch: any) => ({
  getSamlLinkStatus: () => dispatch(actions.getSamlLinkStatus()),
  startSamlLink: (currentPassword: string) => dispatch(actions.startSamlLink(currentPassword)),
  deleteSamlLink: (currentPassword: string) => dispatch(actions.deleteSamlLink(currentPassword)),
});

export default connect(null, mapDispatchToProps)(SamlLinkSettings);
